<template>
	<div
		class="game-control"
		:class="wraperClass"
	>
		<!-- controls container -->
		<div class="controls">
			<!-- (default) home team name label -->
			<div
				v-if="!isMobileVariant"
				class="team-label"
			>
				{{ homeTeamLabel }}
			</div>

			<!-- main area -->
			<div class="main">
				<div class="left">
					<!-- (mobile) home team name label -->
					<div
						v-if="isMobileVariant"
						class="team-label"
					>
						{{ homeTeamLabel }}
					</div>
					<!-- home team score -->
					<button
						class="score-increase"
						@click="handleIncreaseHomeScore"
					>
						<Icon
							icon-name="e-add"
							size="sm"
						/>
					</button>
					<span class="score-value">{{ homeTeamScore }}</span>
					<button
						class="score-decrease"
						@click="handleDecreaseHomeScore"
					>
						<Icon
							icon-name="e-delete"
							size="sm"
						/>
					</button>
				</div>
				<div class="center">
					<!-- time input -->
					<input
						v-model="timeInputValue"
						:readonly="!isTimeEditable || isMobileVariant"
						:class="{ disabled: !isTimeEditable }"
						class="time-set"
						type="text"
						@change="handleInputChange"
						@click="handleInputClick"
					>
					<!-- time start/stop button -->
					<button
						class="time-toggle"
						@click="handleClockToggle"
					>
						<Icon
							:icon-name="timeToggleButtonIcon"
							size="sm"
						/>
					</button>
				</div>
				<div class="right">
					<!-- (mobile) guest team name label -->
					<div
						v-if="isMobileVariant"
						class="team-label"
					>
						{{ guestTeamLabel }}
					</div>
					<!-- guest team score -->
					<button
						class="score-increase"
						@click="handleIncreaseGuestScore"
					>
						<Icon
							icon-name="e-add"
							size="sm"
						/>
					</button>
					<span class="score-value">{{ guestTeamScore }}</span>
					<button
						class="score-decrease"
						@click="handleDecreaseGuestScore"
					>
						<Icon
							icon-name="e-delete"
							size="sm"
						/>
					</button>
				</div>
			</div>

			<!-- (default) guest team name label -->
			<div
				v-if="!isMobileVariant"
				class="team-label"
			>
				{{ guestTeamLabel }}
			</div>
		</div>

		<!-- disabled state overlay -->
		<div
			v-if="isDisabled"
			class="disabled-overlay"
		>
			<div
				v-if="showNotification"
				class="notification"
			>
				<p>{{ transUcfirst('analysis.scoreboard.activation_info') }}</p>
			</div>
		</div>

		<!-- mobile: floating time input control -->
		<div
			v-if="showMobileTimeInput"
			class="mobile-time-input"
		>
			<MobileTimeInput
				v-model="timeInputValue"
				:labels="mobileTimeInputLabels"
				@cancel="handleMobileInputCancel"
			/>
		</div>
	</div>
</template>

<script>
import { padStart } from 'lodash-es';
import { getScoreZones, getScoreZone, getGameTime, getPeriods } from '@js/logic/game';
import { getGameTime as futsalGameTime, getPeriods as futsalPeriods } from '@js/logic/game/futsal';
import MobileTimeInput from '@components/MobileTimeInput/MobileTimeInput.vue';
import { GAME_SPORT } from '@js/constants/game';

const VARIANT = {
	DEFAULT: 'default',
	MOBILE:  'mobile',
};
const STATE = {
	DISABLED: 'disabled',
	ACTIVE:   'active',
	PAUSED:   'paused',
};
const HALFTIME_START = 'halftime_start';
const HALFTIME_END = 'halftime_end';
const PENALTY_SHOOTOUT_START = 'penalty_shootout_start';
const PENALTY_SHOOTOUT_END = 'penalty_shootout_end';
const TIMING_TAG = {
	CLOCK_START: 'clock_start',
	CLOCK_STOP:  'clock_end',
	CLOCK_SET:   'clock_set',
};

const halftimeTagNames = new Set([HALFTIME_START, HALFTIME_END]);
const penaltyShootoutTagNames = new Set([PENALTY_SHOOTOUT_START, PENALTY_SHOOTOUT_END]);
const timingTagNames = Object.values(TIMING_TAG);

export default {
	name:       'GameControl',
	components: { MobileTimeInput },
	props:      {
		position: {
			type:     Number,
			required: true,
		},
		event: {
			type:     Object,
			required: true,
		},
		tags: {
			type:     Array,
			required: true,
		},
		variant: {
			type:    String,
			default: VARIANT.DEFAULT,
		},
		disabled: {
			type: Boolean,
		},
	},
	data() {
		return {
			timeInputValue:      '',
			showMobileTimeInput: false,
		};
	},
	computed: {
		isDisabledFromProp() {
			return this.disabled;
		},
		isMobileVariant() {
			return this.variant === VARIANT.MOBILE;
		},
		hasHalftimeStartTag() {
			return this.tags.some((eventTag) => eventTag.tag.name === HALFTIME_START);
		},
		sortedTags() {
			const sortedTags = Array.from(this.tags);
			sortedTags.sort((a, b) => a.timestamp - b.timestamp);
			return sortedTags;
		},
		timingTags() {
			return this.sortedTags.filter((eventTag) => {
				return [...timingTagNames, ...halftimeTagNames].includes(eventTag.tag.name) && eventTag.timestamp < this.position;
			});
		},
		halftimeTags() {
			return this.sortedTags.filter((eventTag) => halftimeTagNames.has(eventTag.tag.name) && eventTag.timestamp < this.position);
		},
		penaltyShootoutTags() {
			return this.sortedTags.filter((eventTag) => penaltyShootoutTagNames.has(eventTag.tag.name) && eventTag.timestamp < this.position);
		},
		state() {
			// Rule 1: State is DISABLED between halftime_end and halftime_start, or if there is no halftime_start.
			if (!this.hasHalftimeStartTag) return STATE.DISABLED;
			const halfTimeEnded = this.halftimeTags.length > 0 && this.halftimeTags[this.halftimeTags.length - 1].tag.name === HALFTIME_END;
			const penaltyShootoutStarted = this.penaltyShootoutTags.length > 0 && this.penaltyShootoutTags[this.penaltyShootoutTags.length - 1].tag.name === PENALTY_SHOOTOUT_START;
			if (halfTimeEnded && !penaltyShootoutStarted) {
				return STATE.DISABLED;
			}

			// Rule 2: State is ACTIVE if there are no CLOCK_START / CLOCK_STOP tags.
			// Rule 3: State is ACTIVE/PAUSED based on the closest CLOCK_START/CLOCK_STOP.
			for (let i = this.timingTags.length - 1; i >= 0; i--) {
				const currentTag = this.timingTags[i];
				if (currentTag.tag.name === TIMING_TAG.CLOCK_START) return STATE.ACTIVE;
				if (currentTag.tag.name === TIMING_TAG.CLOCK_STOP) return STATE.PAUSED;
			}
			return STATE.ACTIVE;
		},
		isFutsal() {
			return this.event.sport.name === GAME_SPORT.FUTSAL;
		},
		isDisabled() {
			return this.isDisabledFromProp || this.state === STATE.DISABLED;
		},
		isActive() {
			return !this.isDisabled && this.state === STATE.ACTIVE;
		},
		isPaused() {
			return !this.isDisabled && this.state === STATE.PAUSED;
		},
		wraperClass() {
			return {
				[this.state]: true,
				mobile:       this.isMobileVariant,
				disabled:     this.isDisabled,
			};
		},
		homeTeam() {
			return this.event.participants.find((item) => item.type === 'home');
		},
		guestTeam() {
			return this.event.participants.find((item) => item.type === 'guest');
		},
		scoreZones() {
			return getScoreZones({ event: this.event, tags: this.tags });
		},
		currentScore() {
			const zone = getScoreZone({
				timestamp:  this.position,
				event:      this.event,
				tags:       this.tags,
				scoreZones: this.scoreZones,
			});
			const homeScore = Object.values(zone.score).find((item) => item.participant.id === this.homeTeam.id);
			const guestScore = Object.values(zone.score).find((item) => item.participant.id === this.guestTeam.id);

			return {
				home:  homeScore.value,
				guest: guestScore.value,
			};
		},
		periods() {
			if (this.isFutsal) return futsalPeriods({ event: this.event, tags: this.tags });
			return getPeriods({ event: this.event, tags: this.tags });
		},
		currentTime() {
			try {
				let minutes, seconds;
				if (this.isFutsal) {
					({ minutes, seconds } = futsalGameTime({
						position: this.position,
						event:    this.event,
						tags:     this.timingTags,
						periods:  this.periods,
					}));
				} else {
					({ minutes, seconds } = getGameTime({
						position: this.position,
						event:    this.event,
						tags:     this.timingTags,
						periods:  this.periods,
					}));
				}

				const formattedTime = `${padStart(minutes, 2, '0')}:${padStart(Math.floor(seconds), 2, '0')}`;

				return {
					minutes,
					seconds,
					formattedTime,
				};
			} catch {
				console.info('Unable to calculate game time');
			}

			return {
				minutes:       0,
				seconds:       0,
				formattedTime: '-:-',
			};
		},
		homeTeamLabel() {
			return this.homeTeam.participatable.club.code;
		},
		guestTeamLabel() {
			return this.guestTeam.participatable.club.code;
		},
		homeTeamScore() {
			return this.currentScore.home;
		},
		guestTeamScore() {
			return this.currentScore.guest;
		},
		isTimeEditable() {
			return this.state === STATE.PAUSED;
		},
		timeToggleButtonIcon() {
			if (this.state === STATE.ACTIVE) return 'button-pause-filled';
			return 'button-play-filled';
		},
		showNotification() {
			return !this.isDisabledFromProp && !this.hasHalftimeStartTag;
		},
		mobileTimeInputLabels() {
			return {
				title:   this.$I18n.trans('analysis.mobile_time_input_title'),
				confirm: this.$I18n.trans('analysis.mobile_time_input_save'),
			};
		},
	},
	watch: {
		currentTime(value, prevValue) {
			if (value.formattedTime === prevValue.formattedTime) return;
			this.timeInputValue = value.formattedTime;
		},
		timeInputValue() {
			if (this.isMobileVariant && this.showMobileTimeInput) {
				this.handleInputChange();
				this.showMobileTimeInput = false;
			}
		},
	},
	mounted() {
		this.timeInputValue = this.currentTime.formattedTime;
	},
	methods: {
		handleIncreaseHomeScore() {
			this.$emit('score-increase', { participant: this.homeTeam, timestamp: Math.round(this.position) });
		},
		handleDecreaseHomeScore() {
			this.$emit('score-decrease', { participant: this.homeTeam, timestamp: Math.round(this.position) });
		},
		handleIncreaseGuestScore() {
			this.$emit('score-increase', { participant: this.guestTeam, timestamp: Math.round(this.position) });
		},
		handleDecreaseGuestScore() {
			this.$emit('score-decrease', { participant: this.guestTeam, timestamp: Math.round(this.position) });
		},
		handleClockStart() {
			this.$emit('clock-start', { timestamp: Math.round(this.position) });
		},
		handleClockStop() {
			this.$emit('clock-stop', { timestamp: Math.round(this.position) });
		},
		handleClockToggle() {
			if (this.isActive) this.handleClockStop();
			else if (this.isPaused) this.handleClockStart();
			else throw new Error('Tried to toggle the game time in an invalid state.');
		},
		handleInputChange() {
			let minutes = 0;
			let seconds = 0;
			const parts = this.timeInputValue.split(':');
			if (parts.length === 2) {
				minutes = Number.parseInt(parts[0]);
				seconds = Number.parseInt([parts[1]]);
				if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
					const value = `${padStart(Math.max(0, minutes), 2, '0')}:${padStart(Math.max(0, Math.min(59, seconds)), 2, '0')}`;
					this.timeInputValue = value;
					this.$emit('clock-set', {
						timestamp: Math.round(this.position),
						value:     minutes * 60 + seconds,
					});
					return;
				}
			}
			this.timeInputValue = this.currentTime.formattedTime;
		},
		handleInputClick() {
			if (this.isMobileVariant) this.showMobileTimeInput = true;
		},
		handleMobileInputCancel() {
			this.showMobileTimeInput = false;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.game-control {
	position: relative;
	padding-bottom: 32px;

	&:hover {
		.controls .main .center input.time-set {
			background: var(--color-grey-750);
		}
	}

	&.disabled {
		opacity: 0.5;
	}

	.controls {
		display: flex;

		.main {
			flex: 1;
			display: flex;
			padding: 0 24px;

			.left,
			.right {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: space-between;

				.score-value {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 48px;
					height: 48px;
					margin: 16px 0;
					border: 1px solid var(--color-grey-700);
					border-radius: 2px;
					font-weight: 500;
					font-size: 24px;
					line-height: 32px;
				}
			}

			.center {
				flex: 1;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: flex-end;
				margin: 0 32px;

				input {
					width: 100%;
					padding: 8px 0;
					border-radius: 2px;
					background: var(--color-grey-900);
					text-align: center;
					font-weight: 500;
					font-size: 24px;
					line-height: 32px;

					&.time-set {
						background: var(--color-grey-850);
					}

					&:focus:not(.disabled) {
						outline: 1px solid var(--color-hype-yellow);
					}
				}

				button.time-toggle {
					margin-top: 16px;
				}
			}
		}

		button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			background: var(--color-grey-900);
			border: 1px solid var(--color-grey-300);
			border-radius: 100%;
			font-size: 24px;
			line-height: 0;

			&:hover {
				background: var(--color-grey-850);
			}
		}

		.team-label {
			display: flex;
			align-items: center;
			font-weight: 600;
			font-size: 14px;
			line-height: 20px;
		}
	}

	.disabled-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;

		.notification {
			display: flex;
			align-items: flex-start;
			padding: 24px;
			background: var(--color-green-petrol);

			p {
				flex: 1;
			}

			button.close {
				width: 16px;
				height: 16px;
				margin-left: 28px;
				line-height: 0;
				display: grid;
				place-content: center;
			}
		}
	}

	&.active {
		.center {
			color: var(--color-hype-yellow);
			button {
				border: 1px solid var(--color-hype-yellow);
			}
		}
	}

	&.mobile .controls {
		.main {
			padding: 0;
		}

		.team-label {
			margin-bottom: 24px;
		}

		button {
			width: 48px;
			height: 48px;
		}

		input.time-set {
			padding: 40px 0;
		}
	}
}

.mobile-time-input {
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	z-index: 100;
}
</style>
